import React, { useState, useEffect } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { auth, db } from "../../firebase-config";
import { toast } from "react-toastify";
import "../../assets/styles/ClientShared.css";

const HomePageRevision = () => {
    const [homePageRevisionLink, setHomePageRevisionLink] = useState("");
    const [responses, setResponses] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const questions = [
        "Is the headline clear and attention-grabbing? Does it effectively communicate your business's purpose?",
        "Do the images or graphics align with your brand and message? Are they high quality?",
        "Is the layout visually appealing and easy to follow?",
        "Is the text easy to read and free of errors? Does it convey your key points effectively?",
        "Is the call-to-action (e.g., 'Learn More,' 'Contact Us') clear and prominently placed?",
        "Are the colors and fonts consistent with your brand identity?",
        "Is the spacing and alignment of elements consistent and professional?",
        "Does the page load quickly and function smoothly on both desktop and mobile devices?",
        "Is the layout mobile-friendly? Do all elements display correctly on smaller screens?",
        "Does the overall tone of the homepage reflect your business's values and target audience?",
        "Are any critical sections or content missing that you feel are essential for your customers?",
        "Is there any part of the homepage that confused you or felt unclear?",
        "Are the images, videos, or animations appropriately placed and not distracting?",
        "Do you feel the homepage achieves the goals you have for your website (e.g., converting visitors, providing information, building trust)?",
        "What specific changes, if any, would you like to see in the next iteration?",
        "Is there anything else you'd like to add or address that hasn't been covered?",
    ];

    useEffect(() => {
        const fetchHomePageRevisionData = async () => {
            try {
                const currentUser = auth.currentUser;
                if (!currentUser) throw new Error("User not authenticated.");

                const userRef = doc(db, "users", currentUser.uid);
                const userSnap = await getDoc(userRef);

                if (!userSnap.exists()) throw new Error("User document not found.");

                const userData = userSnap.data();
                if (!userData.projectId) throw new Error("Project ID not found.");

                const projectRef = doc(db, "projects", userData.projectId);
                const projectSnap = await getDoc(projectRef);

                if (projectSnap.exists()) {
                    const projectData = projectSnap.data();
                    setHomePageRevisionLink(projectData.revisionDetails?.homePageRevision?.link || "");
                    setResponses(projectData.revisionDetails?.homePageRevision?.form || {});
                } else {
                    throw new Error("Project document not found.");
                }
            } catch (err) {
                console.error(err.message);
                toast.error("Failed to load data.");
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchHomePageRevisionData();
    }, []);

    const handleInputChange = (question, value) => {
        setResponses((prevResponses) => ({
            ...prevResponses,
            [question]: value,
        }));
    };

    const saveResponses = async () => {
        try {
            const currentUser = auth.currentUser;
            if (!currentUser) throw new Error("User not authenticated.");

            const userRef = doc(db, "users", currentUser.uid);
            const userSnap = await getDoc(userRef);

            if (!userSnap.exists()) throw new Error("User document not found.");

            const userData = userSnap.data();
            if (!userData.projectId) throw new Error("Project ID not found.");

            const projectRef = doc(db, "projects", userData.projectId);
            await updateDoc(projectRef, {
                "revisionDetails.homePageRevision.form": responses,
            });

            toast.success("Responses saved successfully!");
        } catch (err) {
            console.error("Error saving responses:", err.message);
            toast.error("Failed to save responses. Please try again.");
        }
    };

    return (
        <div className="cp-page cp-page--narrow">
            <div className="cp-page-header">
                <p className="cp-eyebrow">Client Portal</p>
                <h1 className="cp-title">Home Page Revision</h1>
            </div>

            {loading ? (
                <div className="cp-loading"><p>Loading...</p></div>
            ) : (
                <>
                    {error && <p className="cp-message--error">{error}</p>}

                    <div className="cp-section" style={{marginBottom:'20px'}}>
                        <div className="cp-section-header"><h2 className="cp-section-title">Preview Link</h2></div>
                        <div className="cp-section-body">
                            {homePageRevisionLink ? (
                                <a
                                    href={homePageRevisionLink.startsWith('http') ? homePageRevisionLink : `https://${homePageRevisionLink}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="cp-doc-link"
                                    style={{fontSize:'0.9375rem'}}
                                >
                                    View Home Page →
                                </a>
                            ) : (
                                <p style={{fontFamily:'var(--font-ui)',fontSize:'0.9375rem',color:'var(--text-secondary)',margin:0}}>No link available yet. Check back soon.</p>
                            )}
                        </div>
                    </div>

                    <div className="cp-section" style={{marginBottom:'20px'}}>
                        <div className="cp-section-header"><h2 className="cp-section-title">Instructions</h2></div>
                        <div className="cp-section-body">
                            <p style={{fontFamily:'var(--font-ui)',fontSize:'0.9375rem',color:'var(--text-secondary)',lineHeight:1.7,marginTop:0}}>
                                Please provide detailed and descriptive feedback for each question. Vague responses make it harder to address your concerns effectively. Below are examples of good and bad responses:
                            </p>
                            <div style={{marginTop:'16px',display:'flex',flexDirection:'column',gap:'8px'}}>
                                <p style={{fontFamily:'var(--font-mono)',fontSize:'0.75rem',textTransform:'uppercase',letterSpacing:'0.06em',color:'#16a34a',margin:0}}>Good</p>
                                <p style={{fontFamily:'var(--font-ui)',fontSize:'0.875rem',color:'var(--text-secondary)',margin:0}}>"The call-to-action button 'Learn More' is clear, but the color doesn't stand out enough against the background. I suggest using a brighter color, like orange."</p>
                                <p style={{fontFamily:'var(--font-mono)',fontSize:'0.75rem',textTransform:'uppercase',letterSpacing:'0.06em',color:'var(--accent)',margin:'8px 0 0'}}>Avoid</p>
                                <p style={{fontFamily:'var(--font-ui)',fontSize:'0.875rem',color:'var(--text-secondary)',margin:0}}>"The button looks okay, I guess."</p>
                                <p style={{fontFamily:'var(--font-mono)',fontSize:'0.75rem',textTransform:'uppercase',letterSpacing:'0.06em',color:'#16a34a',margin:'8px 0 0'}}>Good</p>
                                <p style={{fontFamily:'var(--font-ui)',fontSize:'0.875rem',color:'var(--text-secondary)',margin:0}}>"The headline is engaging, but it could be more specific. For example, instead of 'Welcome,' it could say 'Welcome to Your One-Stop Shop for Home Decor.'"</p>
                                <p style={{fontFamily:'var(--font-mono)',fontSize:'0.75rem',textTransform:'uppercase',letterSpacing:'0.06em',color:'var(--accent)',margin:'8px 0 0'}}>Avoid</p>
                                <p style={{fontFamily:'var(--font-ui)',fontSize:'0.875rem',color:'var(--text-secondary)',margin:0}}>"The headline is fine."</p>
                            </div>
                        </div>
                    </div>

                    <div className="cp-section" style={{marginBottom:'20px'}}>
                        <div className="cp-section-header"><h2 className="cp-section-title">Feedback Questions</h2></div>
                        <div className="cp-section-body">
                            {questions.map((question, index) => (
                                <div key={index} className="cp-form-group">
                                    <label>{question}</label>
                                    <textarea
                                        value={responses[question] || ''}
                                        onChange={e => handleInputChange(question, e.target.value)}
                                        placeholder="Write your response here..."
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="cp-btn-row">
                        <button className="cp-btn-primary" onClick={saveResponses}>Save Responses</button>
                    </div>
                </>
            )}
        </div>
    );
};

export default HomePageRevision;
